"use client";

import { RequestInfo } from "rwsdk/worker";
import { AppLayout } from "@/components/layouts";
import { Shield, Lock, Eye, Server, Key, UserCheck } from "lucide-react";

export function Security({ ctx }: RequestInfo) {
  return (
    <AppLayout ctx={ctx}>
      <div className="container mx-auto px-4 py-12 sm:py-16 max-w-3xl">
        <div className="mb-10">
          <div className="w-10 h-10 bg-foreground rounded-full flex items-center justify-center mb-4">
            <Shield className="w-4.5 h-4.5 text-background" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-2">Security & Privacy</h1>
          <p className="text-sm text-muted-foreground">How ForwardCents protects your financial data</p>
        </div>

        {/* Core security features */}
        <div className="grid gap-3 sm:grid-cols-2 mb-8">
          {[
            { icon: Lock, title: "End-to-End Encryption", desc: "All data encrypted in transit (TLS 1.3) and at rest (AES-256). Financial information is never stored in plain text." },
            { icon: Eye, title: "PII Removal", desc: "Personal identifiers are stripped from emails before AI processing. Your name, address, and card numbers are removed." },
            { icon: Server, title: "Secure Infrastructure", desc: "Built on Cloudflare Workers with enterprise-grade security, DDoS protection, and global edge network." },
            { icon: UserCheck, title: "Data Isolation", desc: "Your expense data is completely isolated. Strict user-based access controls enforce this at every level." },
          ].map((item) => (
            <div key={item.title} className="border rounded-xl p-5">
              <item.icon className="w-4 h-4 text-muted-foreground mb-3" />
              <h3 className="text-sm font-medium mb-1.5">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* How emails are handled */}
        <div className="border rounded-xl p-5 mb-5">
          <h2 className="text-base font-medium mb-4">How Your Emails Are Handled</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { step: "1", title: "Email Received", desc: "You forward a receipt to your unique ForwardCents address" },
              { step: "2", title: "PII Removed", desc: "Personal information is automatically stripped before processing" },
              { step: "3", title: "Email Deleted", desc: "Original email is permanently deleted after data extraction" },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-8 h-8 rounded-full bg-foreground text-background text-xs font-medium flex items-center justify-center mx-auto mb-2.5">
                  {item.step}
                </div>
                <span className="text-sm font-medium">{item.title}</span>
                <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Security measures */}
        <div className="border rounded-xl p-5 mb-5">
          <h2 className="text-base font-medium mb-4">Security Measures</h2>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <Key className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-sm font-medium">Authentication & Access</span>
              </div>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li>Google OAuth for secure sign-in</li>
                <li>No passwords to manage or compromise</li>
                <li>Session-based authentication with automatic expiry</li>
                <li>Unique email addresses for each user</li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-sm font-medium">Data Protection</span>
              </div>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li>AES-256 encryption for stored data</li>
                <li>TLS 1.3 for data in transit</li>
                <li>Regular security audits and monitoring</li>
                <li>Automated backup and disaster recovery</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Privacy by design */}
        <div className="border rounded-xl p-5 mb-5">
          <h2 className="text-base font-medium mb-4">Privacy by Design</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="p-4 border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-950/10 rounded-lg">
              <span className="text-sm font-medium mb-2 block">What we do</span>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>Extract only expense-related data</li>
                <li>Encrypt all stored information</li>
                <li>Allow you to edit or delete any data</li>
                <li>Provide transparent data handling</li>
                <li>Delete original emails after processing</li>
              </ul>
            </div>
            <div className="p-4 border border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-950/10 rounded-lg">
              <span className="text-sm font-medium mb-2 block">What we don't do</span>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>Store original email content</li>
                <li>Share or sell your data</li>
                <li>Connect to your bank accounts</li>
                <li>Scan your personal email inbox</li>
                <li>Track browsing or personal activities</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div className="border rounded-xl p-5 mb-5">
          <h2 className="text-base font-medium mb-4">Compliance & Standards</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { label: "Security", title: "Industry Standards", desc: "Best practices for financial data handling and cloud security" },
              { label: "Privacy", title: "GDPR Ready", desc: "Right to deletion and data portability built in" },
              { label: "Transparency", title: "Open Communication", desc: "Clear documentation and responsive to inquiries" },
            ].map((item) => (
              <div key={item.label} className="p-3.5 bg-muted/30 rounded-lg">
                <span className="text-xs text-muted-foreground border rounded-full px-2 py-0.5">{item.label}</span>
                <h4 className="text-sm font-medium mt-2 mb-0.5">{item.title}</h4>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Report */}
        <div className="border rounded-xl p-5">
          <h2 className="text-base font-medium mb-2">Report Security Issues</h2>
          <p className="text-sm text-muted-foreground mb-3">
            If you discover a vulnerability or have concerns, please contact us immediately.
          </p>
          <div className="p-3.5 bg-muted/30 rounded-lg space-y-1.5 text-sm text-muted-foreground">
            <div><span className="font-medium text-foreground">Email:</span> <a href="mailto:hey@harshil.dev" className="underline">hey@harshil.dev</a></div>
            <div><span className="font-medium text-foreground">Response:</span> Within 24 hours for security issues</div>
            <div><span className="font-medium text-foreground">Disclosure:</span> We appreciate responsible disclosure</div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            For details on how we handle your data, see our <a href="/privacy" className="underline hover:text-foreground transition-colors">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
